// v2 sheep — a proper fleece roll: a big cloud body built from overlapping wool
// lobes with extra lumps clouding the back, slim dark legs joined at the hip, a
// dark face carried on a wool collar with the signature wool topknot on the
// crown, drooping ears, pale eyes on the dark mask and a wool nub tail. Three
// flocks: cream blackface, ivory brownface and a dark "black sheep". Ported 1:1
// from age-of-mcp's v2/sheep.ts, using the library's shared `critter` (quadLegs)
// helper.
import * as THREE from "three";
import { defineModel, critter, type Part } from "../../lib";

const FLOCKS = [
  { wool: "#e9e1cd", woolHi: "#f5efe0", face: "#2e2a26", leg: "#28241f", eye: "#d8d0c0" },
  { wool: "#e0d4ba", woolHi: "#ece2cc", face: "#6a4e34", leg: "#54402c", eye: "#e6dcc8" },
  { wool: "#4e463c", woolHi: "#60564a", face: "#211d18", leg: "#1d1a15", eye: "#c2b8a4" },
];

function sheepParts(v: number): Part[] {
  const c = FLOCKS[v % FLOCKS.length]!;
  const parts: Part[] = critter.quadLegs({
    dx: 0.052, dz: 0.03, bodyY: 0.098, rTop: 0.0068, rFoot: 0.0052, color: c.leg,
  });
  parts.push(
    // the fleece roll — main mass, shoulder and rump lobes
    { geometry: new THREE.IcosahedronGeometry(0.054, 0), color: c.wool,
      position: [-0.004, 0.098, 0], scale: [1.5, 1.05, 1.15] },
    { geometry: new THREE.IcosahedronGeometry(0.036, 0), color: c.woolHi,
      position: [0.046, 0.104, 0], scale: [1.0, 1.0, 1.05] },
    { geometry: new THREE.IcosahedronGeometry(0.037, 0), color: c.wool,
      position: [-0.054, 0.102, 0], scale: [1.0, 1.0, 1.05] },
    // wool lumps clouding the back
    { geometry: new THREE.IcosahedronGeometry(0.019, 0), color: c.woolHi,
      position: [0.018, 0.138, 0.018] },
    { geometry: new THREE.IcosahedronGeometry(0.02, 0), color: c.wool,
      position: [-0.02, 0.142, -0.014] },
    { geometry: new THREE.IcosahedronGeometry(0.018, 0), color: c.woolHi,
      position: [-0.05, 0.132, 0.02] },
    // wool collar the head pokes out of
    { geometry: new THREE.IcosahedronGeometry(0.023, 0), color: c.woolHi,
      position: [0.072, 0.108, 0] },
    // dark face: skull, roman-nosed muzzle, pale eyes
    { geometry: new THREE.IcosahedronGeometry(0.0205, 0), color: c.face,
      position: [0.094, 0.104, 0], scale: [1.2, 1.05, 0.85] },
    { geometry: new THREE.IcosahedronGeometry(0.0125, 0), color: c.face,
      position: [0.113, 0.097, 0], scale: [1.25, 0.85, 0.75] },
    { geometry: new THREE.IcosahedronGeometry(0.003, 0), color: c.eye,
      position: [0.102, 0.111, 0.0155] },
    { geometry: new THREE.IcosahedronGeometry(0.003, 0), color: c.eye,
      position: [0.102, 0.111, -0.0155] },
    // the signature wool topknot on the crown
    { geometry: new THREE.IcosahedronGeometry(0.0135, 0), color: c.woolHi,
      position: [0.092, 0.124, 0] },
    // drooping ears — flattened blades tipped down and out
    { geometry: new THREE.IcosahedronGeometry(0.0095, 0), color: c.face,
      position: [0.088, 0.112, 0.0185], scale: [0.9, 0.5, 1.6], rotation: [0.55, 0, 0] },
    { geometry: new THREE.IcosahedronGeometry(0.0095, 0), color: c.face,
      position: [0.088, 0.112, -0.0185], scale: [0.9, 0.5, 1.6], rotation: [-0.55, 0, 0] },
    // wool nub tail
    { geometry: new THREE.IcosahedronGeometry(0.0125, 0), color: c.woolHi,
      position: [-0.094, 0.105, 0] },
  );
  return parts;
}

export const sheepV2 = defineModel({
  id: "sheep-v2",
  scale: 2.21, // real-world size correction (see Model.scale)
  batchId: "fable",
  name: "Sheep (v2)",
  tags: ["creature","sheep","low-poly","age-of-mcp","fable"],
  summary: "Low-poly sheep.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.13,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => sheepParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => sheepParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => sheepParts(2) },
  ],
  animations: [],
});
